import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import BadgeRow from "@/components/BadgeRow";
import TitleTag from "@/components/TitleTag";
import { FlagBadge } from "@/lib/flags";
import BlurImage from "@/components/BlurImage";

/**
 * Floating chat sidebar with Global + Guild channel switcher.
 * Plays /sounds/message.mp3 on new arrivals in the active channel from other users.
 */
export default function GlobalChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("global"); // global | guild
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [unread, setUnread] = useState(0);
  const [err, setErr] = useState("");
  const [hasGuild, setHasGuild] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const isAdmin = user?.role === "admin";
  const lastSeenRef = useRef(null);
  const bottomRef = useRef(null);
  const audioRef = useRef(null);
  const lastSentAtRef = useRef(0);
  // V2.2.0: chats are now character-based (not word-based). Global = 35 chars, Guild = 50.
  const CHANNEL_LIMIT = channel === "global" ? 35 : 50;

  useEffect(() => {
    try { audioRef.current = new Audio("/sounds/message.mp3"); audioRef.current.preload = "auto"; audioRef.current.volume = 0.6; } catch {}
  }, []);
  function pingSound() {
    try { const a = audioRef.current; if (!a) return; a.currentTime = 0; const p = a.play(); if (p && p.catch) p.catch(() => {}); } catch {}
  }

  // Detect guild membership
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/guilds/mine"); setHasGuild(!!data?.id); } catch {} })();
  }, [open]);

  // Poll messages of active channel
  useEffect(() => {
    let cancelled = false;
    lastSeenRef.current = null;
    async function tick() {
      try {
        const url = channel === "global" ? "/social/chat" : "/social/guild-chat";
        const { data } = await api.get(url, { params: { limit: 60 } });
        if (cancelled) return;
        const prevLast = lastSeenRef.current;
        const last = data[data.length - 1];
        if (last && prevLast && last.created_at > prevLast) {
          const newOnes = data.filter(m => m.created_at > prevLast && m.from_id !== user?.id);
          if (newOnes.length > 0) {
            pingSound();
            if (!open) setUnread(u => u + newOnes.length);
          }
        }
        setMsgs(data);
        if (last) {
          if (!prevLast) lastSeenRef.current = last.created_at;
          else if (open) lastSeenRef.current = last.created_at;
        }
      } catch {}
    }
    tick();
    const id = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, channel, user?.id]);

  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => bottomRef.current?.scrollIntoView({ block: "end" }), 100);
      if (msgs.length) lastSeenRef.current = msgs[msgs.length - 1].created_at;
    }
  }, [open, msgs]);

  async function send(e) {
    e.preventDefault();
    if (!draft.trim() && !pendingImage) return;
    const chars = draft.length;
    if (draft.trim() && chars > CHANNEL_LIMIT) {
      setErr(`Character limit ${chars}/${CHANNEL_LIMIT}`); setTimeout(() => setErr(""), 2500); return;
    }
    const now = Date.now();
    if (now - lastSentAtRef.current < 5000) {
      const wait = Math.ceil((5000 - (now - lastSentAtRef.current)) / 1000);
      setErr(`Slow down — wait ${wait}s`); setTimeout(() => setErr(""), 2500); return;
    }
    try {
      const url = channel === "global" ? "/social/chat" : "/social/guild-chat";
      await api.post(url, { body: draft, image_data_url: pendingImage });
      lastSentAtRef.current = Date.now();
      setDraft(""); setPendingImage(null);
      const { data } = await api.get(url, { params: { limit: 60 } });
      setMsgs(data);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Send failed");
      setTimeout(() => setErr(""), 4000);
    }
  }

  function onPickImage(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { setErr("Image file only"); setTimeout(() => setErr(""), 2500); return; }
    if (f.size > 400_000) { setErr("Image must be under 400 KB"); setTimeout(() => setErr(""), 2500); return; }
    const r = new FileReader();
    r.onload = () => setPendingImage(r.result);
    r.readAsDataURL(f);
  }

  async function deleteMessage(id) {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/social/chat/${id}`); setMsgs(m => m.filter(x => x.id !== id)); } catch {}
  }

  async function pinMessage(id) {
    try { await api.post("/social/chat/pin", { message_id: id, minutes: 1 }); setErr("📌 Pinned for 1 minute"); setTimeout(() => setErr(""), 2500); } catch {}
  }

  const charCount = draft.length;
  const overLimit = charCount > CHANNEL_LIMIT;

  return (
    <>
      <button data-testid="global-chat-toggle" onClick={() => setOpen(o => !o)}
        className={`fixed bottom-5 right-5 z-30 w-12 h-12 rounded-full shadow-2xl flex items-center justify-center transition-all ${
          open ? "bg-slate-800 text-slate-300" : "bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:scale-110"}`}>
        <i className={`fa-solid ${open ? "fa-xmark" : "fa-comments"} text-lg`}/>
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-black w-5 h-5 rounded-full flex items-center justify-center animate-pulse">{unread}</span>
        )}
      </button>

      <aside data-testid="global-chat-panel" className={`fixed top-0 right-0 z-30 h-full w-80 bg-[#0f131b] border-l border-slate-800 flex flex-col font-mono transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>
        <header className="px-3 py-2.5 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-comments text-indigo-300"/>
            <div className="text-xs font-bold text-white">{channel === "global" ? "Global" : "Guild"} Chat</div>
          </div>
          <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-rose-300"><i className="fa-solid fa-xmark"/></button>
        </header>
        {/* Channel switcher */}
        <div className="flex border-b border-slate-800 text-[10px] font-bold">
          <button data-testid="chat-channel-global" onClick={() => setChannel("global")}
            className={`flex-1 py-1.5 ${channel === "global" ? "bg-indigo-500/10 text-indigo-200" : "text-slate-500"}`}>GLOBAL</button>
          <button data-testid="chat-channel-guild" onClick={() => hasGuild && setChannel("guild")} disabled={!hasGuild}
            className={`flex-1 py-1.5 ${channel === "guild" ? "bg-indigo-500/10 text-indigo-200" : "text-slate-500 disabled:opacity-40"}`}>GUILD {!hasGuild && "🔒"}</button>
        </div>

        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {/* Pinned strip */}
          {msgs.some(m => m.pinned_until && m.pinned_until > new Date().toISOString()) && (
            <div className="text-[10px] text-amber-300 uppercase font-bold mb-1"><i className="fa-solid fa-thumbtack mr-1"/>Pinned</div>
          )}
          {msgs.length === 0 && <div className="text-center text-slate-600 text-xs mt-6">{channel === "guild" ? "Your guild's chat is empty." : "Be the first to break the silence."}</div>}
          {msgs.map(m => {
            const isPinned = m.pinned_until && m.pinned_until > new Date().toISOString();
            return (
              <div key={m.id} data-testid={`chat-msg-${m.id}`} className={`flex gap-2 group ${isPinned ? "bg-amber-500/5 border border-amber-500/20 rounded p-1.5" : ""}`}>
                <Link to={`/u/${m.from_id}`}>
                  {m.from_avatar
                    ? <img src={m.from_avatar} alt="" className="w-7 h-7 rounded-full object-cover border border-slate-700"/>
                    : <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-[10px] text-slate-500">{(m.from_name||"?").charAt(0).toUpperCase()}</div>}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    {m.from_country && <FlagBadge code={m.from_country} size="sm"/>}
                    <Link to={`/u/${m.from_id}`} style={m.from_name_color ? { color: m.from_name_color } : undefined}
                      className="text-xs font-bold text-white hover:text-amber-300 truncate">{m.from_name}</Link>
                    <TitleTag userId={m.from_id} size="sm"/>
                    <BadgeRow userId={m.from_id} size="sm"/>
                    {isPinned && <i className="fa-solid fa-thumbtack text-amber-300 text-[9px]"/>}
                    <span className="text-[9px] text-slate-600 ml-auto">{(m.created_at || "").slice(11,16)}</span>
                    {isAdmin && channel === "global" && (
                      <span className="opacity-0 group-hover:opacity-100 flex gap-1">
                        <button data-testid={`pin-${m.id}`} onClick={() => pinMessage(m.id)} title="Pin 1 min" className="text-[9px] text-amber-400 hover:text-amber-300"><i className="fa-solid fa-thumbtack"/></button>
                        <button data-testid={`del-${m.id}`} onClick={() => deleteMessage(m.id)} title="Delete" className="text-[9px] text-rose-400 hover:text-rose-300"><i className="fa-solid fa-trash"/></button>
                      </span>
                    )}
                  </div>
                  {m.body && <div className="text-xs text-slate-200 break-words">{m.body}</div>}
                  {m.image_data_url && <BlurImage src={m.image_data_url} className="mt-1"/>}
                </div>
              </div>
            );
          })}
          <div ref={bottomRef}/>
        </div>
        {err && <div className="text-[10px] text-amber-300 bg-amber-500/10 border-t border-amber-500/30 px-2 py-1">{err}</div>}
        {pendingImage && (
          <div className="border-t border-slate-800 px-2 py-1.5 flex items-center gap-2 bg-slate-900/40">
            <img src={pendingImage} alt="" className="w-8 h-8 rounded object-cover"/>
            <span className="text-[10px] text-slate-400 flex-1">Image ready</span>
            <button onClick={() => setPendingImage(null)} className="text-rose-300 text-[10px]"><i className="fa-solid fa-xmark"/></button>
          </div>
        )}
        <form onSubmit={send} className="border-t border-slate-800 p-2 flex gap-2">
          <div className="flex-1 relative">
            <input data-testid="chat-input" value={draft} onChange={e => setDraft(e.target.value)} maxLength={CHANNEL_LIMIT * 2}
              placeholder={`Say something… (${CHANNEL_LIMIT}w max)`} className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
            <span className={`absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] ${overLimit ? "text-rose-300 font-bold" : "text-slate-600"}`}>{charCount}/{CHANNEL_LIMIT}</span>
          </div>
          <label data-testid="chat-image-btn" className="px-2 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-xs cursor-pointer text-amber-300" title="Attach image">
            <i className="fa-solid fa-image"/>
            <input type="file" accept="image/*" onChange={onPickImage} className="hidden"/>
          </label>
          <button data-testid="chat-send" type="submit" disabled={overLimit}
            className={`px-3 py-1.5 rounded text-xs font-bold ${overLimit ? "bg-slate-800 text-slate-600" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}><i className="fa-solid fa-paper-plane"/></button>
        </form>
      </aside>
    </>
  );
}
